import { useEffect, useRef, useState } from "react";
import { useOps } from "./useOps";
import { Header } from "./components/Header";
import { SignalFeed } from "./components/SignalFeed";
import { MapView } from "./components/MapView";
import { DetailRail } from "./components/DetailRail";
import { BottomStrip } from "./components/BottomStrip";
import { ChatDrawer } from "./components/ChatDrawer";
import { HandleGate } from "./components/HandleGate";
import { TrialGate } from "./components/TrialGate";
import { WelcomeGate } from "./components/WelcomeGate";
import { Footer } from "./components/Footer";
import { Toasts } from "./components/Toasts";
import { BootLoader } from "./components/BootLoader";

/**
 * OperationsScreen — the heart of the product. Composes the full ops console:
 * header, signal feed (left), map (center), detail rail (right), bottom strip,
 * footer, toasts, and the boot loader.
 *
 * Resizable rails: the feed and detail widths are CSS variables on #stage;
 * drag handles update them and re-fit the map projection.
 */
export function OperationsScreen() {
  const ops = useOps();
  const stageRef = useRef<HTMLDivElement | null>(null);

  // Alert deep links land here as /ops?signal=<id> (delivery format.ts). Select the signal the
  // moment the snapshot delivers it, then scrub the param. Without this, every pushed alert's
  // "Open in dashboard" opened a desk with nothing selected.
  const [deepLinkId, setDeepLinkId] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get("signal"),
  );
  useEffect(() => {
    if (!deepLinkId) return;
    if (!ops.signals.some((x) => x.id === deepLinkId)) return; // snapshot not in yet — retry on next version bump
    ops.selectSignal(deepLinkId);
    const p = new URLSearchParams(window.location.search);
    p.delete("signal");
    window.history.replaceState(null, "", window.location.pathname + (p.toString() ? `?${p}` : ""));
    setDeepLinkId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkId, ops.version]);

  // drag-to-resize the side rails
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const MIN_F = 300, MAX_F = 620, MIN_D = 280, MAX_D = 560;
    let cleanup: Array<() => void> = [];

    const wire = (handleSel: string, which: "feed" | "detail") => {
      const handle = stage.querySelector<HTMLDivElement>(handleSel);
      if (!handle) return;
      const onDown = (e: MouseEvent) => {
        e.preventDefault();
        handle.classList.add("drag");
        document.body.classList.add("col-resizing");
        const move = (ev: MouseEvent) => {
          const r = stage.getBoundingClientRect();
          const sx = stage.clientWidth / r.width;
          if (which === "feed") {
            const w = Math.max(MIN_F, Math.min(MAX_F, (ev.clientX - r.left) * sx));
            stage.style.setProperty("--feed-w", `${w}px`);
          } else {
            const w = Math.max(MIN_D, Math.min(MAX_D, (r.right - ev.clientX) * sx));
            stage.style.setProperty("--detail-w", `${w}px`);
          }
          ops.getMap()?.resize();
        };
        const up = () => {
          handle.classList.remove("drag");
          document.body.classList.remove("col-resizing");
          window.removeEventListener("mousemove", move);
          window.removeEventListener("mouseup", up);
        };
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
      };
      handle.addEventListener("mousedown", onDown);
      cleanup.push(() => handle.removeEventListener("mousedown", onDown));
    };

    wire("#feedHandle", "feed");
    wire("#detailHandle", "detail");
    return () => cleanup.forEach((fn) => fn());
  }, [ops]);

  return (
    <div id="viewport">
      <div id="stage" ref={stageRef} data-screen-label="EARLYWIRE OPS">
        <Header ops={ops} />
        <SignalFeed ops={ops} />
        <MapView ops={ops} />
        <DetailRail ops={ops} />
        <Footer ops={ops} />
        <BottomStrip ops={ops} />
        <ChatDrawer ops={ops} />
        <HandleGate />
        <TrialGate />
        <WelcomeGate />
        <Toasts ops={ops} />
        <BootLoader ops={ops} />
        <div className="tex grain" />
        <div className="tex scan" />
        <div className="tex vign" />
      </div>
    </div>
  );
}
